import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ---------- Schema: defines every editable field per section ----------
// type: 'text' | 'textarea' | 'list'
// For 'list', `item` describes each item's shape.
const SCHEMA = [
  {
    id: 'home_hero',
    label: 'Home — Hero Slider',
    fields: [
      { key: 'hero_slides', type: 'list', label: 'Hero slides', item: {
          fields: [
            { key: 'image', type: 'text', label: 'Image URL (relative or absolute)' },
            { key: 'eyebrow', type: 'text', label: 'Eyebrow (small uppercase label)' },
            { key: 'title', type: 'textarea', label: 'Title (h1)', rows: 2 },
            { key: 'subtitle', type: 'textarea', label: 'Subtitle', rows: 3 },
            { key: 'cta_label', type: 'text', label: 'CTA button label' },
            { key: 'cta_to', type: 'text', label: 'CTA target (path e.g. /register)' },
          ]
        }
      },
    ],
  },
  {
    id: 'home_logos',
    label: 'Home — Client Logos',
    fields: [
      { key: 'client_logos_label', type: 'text', label: 'Section label' },
      { key: 'client_logos', type: 'list', label: 'Logos', item: { fields: [
        { key: 'name', type: 'text', label: 'Client name (wordmark)' },
      ]}},
    ],
  },
  {
    id: 'home_features',
    label: 'Home — Feature Highlights',
    fields: [
      { key: 'features_eyebrow', type: 'text', label: 'Eyebrow' },
      { key: 'features_title', type: 'textarea', label: 'Section title (h2)', rows: 2 },
      { key: 'features', type: 'list', label: 'Feature cards', item: { fields: [
        { key: 'title', type: 'text', label: 'Card title' },
        { key: 'body', type: 'textarea', label: 'Card body', rows: 3 },
      ]}},
    ],
  },
  {
    id: 'how_it_works',
    label: 'Home & About — How it works',
    fields: [
      { key: 'how_it_works', type: 'list', label: 'Steps (used on Home strip + About page)', item: { fields: [
        { key: 'step', type: 'text', label: 'Step number (e.g. 01)' },
        { key: 'title', type: 'text', label: 'Step title' },
        { key: 'body', type: 'textarea', label: 'Step body', rows: 3 },
      ]}},
    ],
  },
  {
    id: 'home_testimonials',
    label: 'Home — Testimonials',
    fields: [
      { key: 'testimonials_eyebrow', type: 'text', label: 'Eyebrow' },
      { key: 'testimonials_title', type: 'textarea', label: 'Section title', rows: 2 },
      { key: 'testimonials', type: 'list', label: 'Testimonials', item: { fields: [
        { key: 'quote', type: 'textarea', label: 'Quote', rows: 4 },
        { key: 'name', type: 'text', label: 'Author name' },
        { key: 'role', type: 'text', label: 'Author role / company' },
        { key: 'initials', type: 'text', label: 'Avatar initials (2 letters)' },
      ]}},
    ],
  },
  {
    id: 'home_cta',
    label: 'Home — Final CTA',
    fields: [
      { key: 'final_cta_title', type: 'textarea', label: 'CTA title', rows: 2 },
      { key: 'final_cta_subtitle', type: 'text', label: 'CTA subtitle' },
    ],
  },
  {
    id: 'about_page',
    label: 'About page',
    fields: [
      { key: 'about_eyebrow', type: 'text', label: 'Hero eyebrow' },
      { key: 'about_title', type: 'textarea', label: 'Hero title', rows: 2 },
      { key: 'about_intro', type: 'textarea', label: 'Hero intro paragraph', rows: 4 },
      { key: 'about_how_eyebrow', type: 'text', label: '"How it works" eyebrow' },
      { key: 'about_how_title', type: 'textarea', label: '"How it works" title', rows: 2 },
      { key: 'about_how_subtitle', type: 'textarea', label: '"How it works" subtitle', rows: 2 },
      { key: 'about_mission_eyebrow', type: 'text', label: 'Mission strip eyebrow' },
      { key: 'about_mission_title', type: 'text', label: 'Mission strip title' },
      { key: 'about_mission_body', type: 'textarea', label: 'Mission strip body', rows: 4 },
      { key: 'about_values_eyebrow', type: 'text', label: 'Values eyebrow' },
      { key: 'about_values_title', type: 'textarea', label: 'Values title', rows: 2 },
      { key: 'about_values', type: 'list', label: 'Value cards', item: { fields: [
        { key: 'title', type: 'text', label: 'Card title' },
        { key: 'body', type: 'textarea', label: 'Card body', rows: 3 },
      ]}},
    ],
  },
  {
    id: 'packages_page',
    label: 'Packages page',
    fields: [
      { key: 'packages_eyebrow', type: 'text', label: 'Hero eyebrow' },
      { key: 'packages_title', type: 'text', label: 'Hero title' },
      { key: 'packages_subtitle', type: 'textarea', label: 'Hero subtitle', rows: 2 },
      { key: 'packages_faq', type: 'list', label: 'FAQ', item: { fields: [
        { key: 'q', type: 'text', label: 'Question' },
        { key: 'a', type: 'textarea', label: 'Answer', rows: 3 },
      ]}},
    ],
  },
  {
    id: 'contact_page',
    label: 'Contact page',
    fields: [
      { key: 'contact_eyebrow', type: 'text', label: 'Hero eyebrow' },
      { key: 'contact_title', type: 'textarea', label: 'Hero title', rows: 2 },
      { key: 'contact_subtitle', type: 'textarea', label: 'Hero subtitle', rows: 3 },
      { key: 'contact_email', type: 'text', label: 'Public email (shown + receives form)' },
      { key: 'contact_hours', type: 'text', label: 'Support hours' },
      { key: 'contact_location', type: 'text', label: 'Location / city' },
    ],
  },
  {
    id: 'footer',
    label: 'Footer',
    fields: [
      { key: 'footer_tagline', type: 'textarea', label: 'Footer tagline', rows: 3 },
    ],
  },
];

// ---------- Field renderers ----------
const TextField = ({ value, onChange, testid }) => (
  <input
    type="text"
    data-testid={testid}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  />
);

const TextArea = ({ value, onChange, rows = 3, testid }) => (
  <textarea
    data-testid={testid}
    value={value ?? ''}
    rows={rows}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
  />
);

const Field = ({ field, value, onChange, testid }) => {
  if (field.type === 'textarea') {
    return <TextArea value={value} onChange={onChange} rows={field.rows} testid={testid} />;
  }
  return <TextField value={value} onChange={onChange} testid={testid} />;
};

const ListEditor = ({ items, itemSchema, onChange, listKey, token }) => {
  const list = Array.isArray(items) ? items : [];
  const updateAt = (idx, newItem) => onChange(list.map((it, i) => (i === idx ? newItem : it)));
  const removeAt = (idx) => onChange(list.filter((_, i) => i !== idx));
  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...list]; [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]; onChange(next);
  };
  const moveDown = (idx) => {
    if (idx === list.length - 1) return;
    const next = [...list]; [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]; onChange(next);
  };
  const addItem = () => {
    const empty = {};
    itemSchema.fields.forEach((f) => { empty[f.key] = ''; });
    onChange([...list, empty]);
  };

  const uploadImage = async (idx, file, fieldKey) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API}/website-content/upload-image`, form, {
        headers: { 'Content-Type': 'multipart/form-data', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const dataUrl = res.data?.data_url;
      if (dataUrl) updateAt(idx, { ...list[idx], [fieldKey]: dataUrl });
    } catch (e) {
      alert('Upload failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-3">
      {list.map((item, idx) => (
        <div key={idx} data-testid={`cms-list-${listKey}-item-${idx}`} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Item {idx + 1}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => moveUp(idx)} disabled={idx === 0} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white disabled:opacity-30">↑</button>
              <button type="button" onClick={() => moveDown(idx)} disabled={idx === list.length - 1} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-white disabled:opacity-30">↓</button>
              <button type="button" data-testid={`cms-list-${listKey}-remove-${idx}`} onClick={() => removeAt(idx)} className="px-2 py-1 text-xs rounded border border-red-300 text-red-700 hover:bg-red-50">Remove</button>
            </div>
          </div>
          <div className="space-y-3">
            {itemSchema.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-gray-700 mb-1">{f.label}</label>
                <Field
                  field={f}
                  value={item[f.key]}
                  onChange={(v) => updateAt(idx, { ...item, [f.key]: v })}
                  testid={`cms-list-${listKey}-${idx}-${f.key}`}
                />
                {/* Image upload helper: shown only for hero_slides "image" field */}
                {listKey === 'hero_slides' && f.key === 'image' && (
                  <div className="mt-2 flex items-center gap-3">
                    <label className="text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 cursor-pointer">
                      Upload image…
                      <input
                        type="file"
                        accept="image/*"
                        data-testid={`cms-list-${listKey}-${idx}-image-upload`}
                        className="hidden"
                        onChange={(e) => uploadImage(idx, e.target.files?.[0], f.key)}
                      />
                    </label>
                    {item[f.key] && (
                      <img
                        src={item[f.key].startsWith('data:') ? item[f.key] : item[f.key]}
                        alt="preview"
                        className="h-12 w-20 object-cover rounded border border-gray-200"
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={addItem} data-testid={`cms-list-${listKey}-add`} className="px-3 py-2 text-sm font-medium rounded-md border border-dashed border-gray-400 text-gray-700 hover:bg-gray-50 w-full">
        + Add item
      </button>
    </div>
  );
};

// ---------- Main component ----------
export const WebsiteCMS = ({ token }) => {
  const [content, setContent] = useState(null);
  const [originalContent, setOriginalContent] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', text: '' });

  const load = useCallback(async () => {
    try {
      const [contentRes, defaultsRes] = await Promise.all([
        axios.get(`${API}/website-content`),
        axios.get(`${API}/website-content/defaults`),
      ]);
      setContent(contentRes.data);
      setOriginalContent(JSON.parse(JSON.stringify(contentRes.data)));
      setDefaults(defaultsRes.data?.defaults || null);
      setStatus({ type: 'idle', text: '' });
    } catch (e) {
      setStatus({ type: 'error', text: 'Could not load website content.' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (key, value) => setContent((c) => ({ ...c, [key]: value }));

  const isDirty = JSON.stringify(content) !== JSON.stringify(originalContent);

  const save = async () => {
    if (!content) return;
    setSaving(true);
    setStatus({ type: 'loading', text: 'Saving…' });
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = { ...content };
      delete payload.id; delete payload.updated_at;
      const res = await axios.put(`${API}/website-content`, payload, { headers });
      setContent(res.data);
      setOriginalContent(JSON.parse(JSON.stringify(res.data)));
      setStatus({ type: 'success', text: 'Saved. Refresh the marketing site to see the changes.' });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Save failed.';
      setStatus({ type: 'error', text: detail });
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!originalContent) return;
    if (window.confirm('Discard all unsaved changes?')) {
      setContent(JSON.parse(JSON.stringify(originalContent)));
      setStatus({ type: 'idle', text: '' });
    }
  };

  const toggleSection = (id) => setOpenSections((s) => ({ ...s, [id]: !s[id] }));

  const resetSectionToDefaults = (section) => {
    if (!defaults) {
      alert('Defaults not loaded yet — please refresh and try again.');
      return;
    }
    const keys = section.fields.map((f) => f.key);
    const label = section.label;
    if (!window.confirm(`Reset "${label}" to factory defaults? This affects only this section; you still need to click Save to publish.`)) return;
    setContent((c) => {
      const next = { ...c };
      keys.forEach((k) => {
        if (k in defaults) {
          next[k] = Array.isArray(defaults[k]) ? JSON.parse(JSON.stringify(defaults[k])) : defaults[k];
        }
      });
      return next;
    });
    setStatus({ type: 'idle', text: `"${label}" reset to defaults. Click Save to publish.` });
  };

  if (!content) {
    return <div data-testid="cms-loading" className="text-center py-12 text-gray-500">Loading website content…</div>;
  }

  return (
    <div data-testid="website-cms" className="space-y-6">
      {/* Header bar */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Website CMS</h2>
            <p className="text-sm text-gray-600 mt-1">
              Edit any text on the public marketing pages (Home, About, Packages, Contact, Footer).
              Changes apply immediately after Save.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={process.env.REACT_APP_BACKEND_URL + '/'}
              target="_blank"
              rel="noreferrer"
              data-testid="cms-preview-link"
              className="px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Preview site ↗
            </a>
            <button
              type="button"
              onClick={reset}
              disabled={!isDirty}
              data-testid="cms-reset"
              className="px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
            >
              Discard changes
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!isDirty || saving}
              data-testid="cms-save"
              className="px-5 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300"
            >
              {saving ? 'Saving…' : isDirty ? 'Save changes' : 'No changes'}
            </button>
          </div>
        </div>
        {status.type !== 'idle' && status.text && (
          <div
            data-testid="cms-status"
            className={`mt-4 px-4 py-2.5 rounded-md text-sm ${
              status.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : status.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`}
          >
            {status.text}
          </div>
        )}
      </div>

      {/* Sections */}
      {SCHEMA.map((section) => {
        const open = !!openSections[section.id];
        return (
          <div key={section.id} data-testid={`cms-section-${section.id}`} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition">
              <button
                type="button"
                data-testid={`cms-section-toggle-${section.id}`}
                onClick={() => toggleSection(section.id)}
                className="flex-1 flex items-center justify-between"
              >
                <span className="font-semibold text-gray-900">{section.label}</span>
                <span className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
              </button>
              {open && (
                <button
                  type="button"
                  onClick={() => resetSectionToDefaults(section)}
                  data-testid={`cms-section-reset-${section.id}`}
                  disabled={!defaults}
                  className="ml-3 px-3 py-1.5 text-xs font-semibold rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                  title="Reset this section's fields to factory defaults"
                >
                  Reset to defaults
                </button>
              )}
            </div>
            {open && (
              <div className="p-5 border-t border-gray-200 space-y-5">
                {section.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-semibold text-gray-800 mb-1.5">{field.label}</label>
                    {field.type === 'list' ? (
                      <ListEditor
                        items={content[field.key]}
                        itemSchema={field.item}
                        listKey={field.key}
                        onChange={(v) => update(field.key, v)}
                        token={token}
                      />
                    ) : (
                      <Field
                        field={field}
                        value={content[field.key]}
                        onChange={(v) => update(field.key, v)}
                        testid={`cms-field-${field.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Sticky-ish footer save bar */}
      {isDirty && (
        <div className="sticky bottom-4 z-10 bg-white border border-blue-300 rounded-lg shadow-lg p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">You have unsaved changes.</span>
          <div className="flex gap-2">
            <button type="button" onClick={reset} className="px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 hover:bg-gray-50">Discard</button>
            <button type="button" onClick={save} disabled={saving} data-testid="cms-save-bottom" className="px-5 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteCMS;
